import React from 'react'
import {
  Alert,
  Button,
  Dialog,
  DialogBody,
  DialogFooter,
  DialogHeader,
  Spinner,
  Typography,
} from '@material-tailwind/react'
import { useTranslation } from 'react-i18next'
import { PiWarningCircle } from 'react-icons/pi'
import { useDarkMode } from '../contexts/DarkModeContext'
import { useWindowSize } from '../hooks/toolkit'
import { useGeneralToast } from '../hooks/toast'
import { deleteGeoXpSpot } from '../utils/helpers/user-spot.helpers'

interface DeleteAgamiDialogProps {
  open: boolean
  userId: string
  agami: {
    id: string
    name: string
    mediaPath: string | null
    mediaType: number
  }
  onClose: () => void
  onDeleted?: () => void | Promise<void>
}

export const DeleteAgamiDialog: React.FC<DeleteAgamiDialogProps> = ({
  open,
  userId,
  agami,
  onClose,
  onDeleted,
}) => {
  const { t } = useTranslation()
  const { darkMode } = useDarkMode()
  const { success: toastSuccess, error: toastError } = useGeneralToast()
  const { width } = useWindowSize()
  const [deleting, setDeleting] = React.useState(false)

  const deleteSpot = async () => {
    setDeleting(true)
    const { error } = await deleteGeoXpSpot({
      userId,
      spotId: agami.id,
      mediaPath: agami.mediaPath,
      mediaType: agami.mediaType,
    })

    if (error) {
      toastError(t('agami.somethingWentWrong'))
      setDeleting(false)
      return
    }

    await onDeleted?.()
    toastSuccess(t('agami.successfullyDeleted'))
    setDeleting(false)
    onClose()
  }

  return (
    <Dialog
      size={(width ?? 0) <= 640 ? 'lg' : 'xs'}
      open={open}
      handler={() => !deleting && onClose()}
      data-testid="delete-agami-dialog"
    >
      <DialogHeader>{t('agami.deleteDialog.title')}</DialogHeader>
      <DialogBody>
        <Alert
          variant={darkMode ? 'outlined' : 'ghost'}
          color="orange"
          icon={<PiWarningCircle className="h-6 w-6" />}
        >
          {t('agami.deleteDialog.warning')}
        </Alert>
        <Typography variant="paragraph" className="my-2">
          {t('agami.deleteDialog.confirmMessage')} <strong className="font-extrabold">{agami.name}</strong>{' '}
          {t('agami.deleteDialog.fromLibrary')}
        </Typography>
      </DialogBody>
      <DialogFooter className="justify-between gap-2">
        <Button size="sm" variant="outlined" onClick={onClose} disabled={deleting}>
          {t('agami.deleteDialog.cancel')}
        </Button>
        <Button
          size="sm"
          color="red"
          onClick={deleteSpot}
          disabled={deleting}
          data-testid="delete-agami-confirm-btn"
        >
          {deleting ? <Spinner className="h-4 w-4" /> : t('agami.deleteDialog.confirmDelete')}
        </Button>
      </DialogFooter>
    </Dialog>
  )
}
